import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface CompanySignupForm {
  company_name: string;
  company_domain: string;
  admin_username: string;
  admin_email: string;
  admin_full_name: string;
  admin_password: string;
  confirm_password: string;
}

const CompanySignup: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState<CompanySignupForm>({
    company_name: '',
    company_domain: '',
    admin_username: '',
    admin_email: '',
    admin_full_name: '',
    admin_password: '',
    confirm_password: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.company_name.trim()) {
      setMessage({ type: 'error', text: 'Company name is required' });
      return false;
    }
    
    if (!formData.admin_username.trim()) {
      setMessage({ type: 'error', text: 'Admin username is required' });
      return false;
    }
    
    if (!formData.admin_email.trim()) {
      setMessage({ type: 'error', text: 'Admin email is required' });
      return false;
    }
    
    if (!formData.admin_full_name.trim()) {
      setMessage({ type: 'error', text: 'Admin full name is required' });
      return false;
    }
    
    if (formData.admin_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return false;
    }
    
    if (formData.admin_password !== formData.confirm_password) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${API_BASE}/signup/user/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: formData.company_name,
          company_domain: formData.company_domain || undefined,
          admin_username: formData.admin_username,
          admin_email: formData.admin_email,
          admin_full_name: formData.admin_full_name,
          admin_password: formData.admin_password,
          plan_tier: "professional",
          role: "company_admin"
        }),
      });
      
      if (response.ok) {
        await response.json();
        // Attempt auto-login immediately
        try {
          const fd = new FormData();
          fd.append('username', formData.admin_username);
          fd.append('password', formData.admin_password);
          const loginResp = await fetch(`${API_BASE}/token`, { method: 'POST', body: fd });
          if (loginResp.ok) {
            const data = await loginResp.json();
            localStorage.setItem('token', data.access_token);
            localStorage.setItem('refreshToken', data.refresh_token);
            localStorage.setItem('user', JSON.stringify(data.user));
            login(data.access_token, data.refresh_token, data.user);
            navigate('/dashboard');
            return;
          }
        } catch (err) {
          // proceed to show success message if auto-login fails
        }
        setMessage({ 
          type: 'success', 
          text: `Company "${formData.company_name}" created successfully! You can now log in with your admin credentials.` 
        });
        
        // Reset form
        setFormData({
          company_name: '',
          company_domain: '',
          admin_username: '',
          admin_email: '',
          admin_full_name: '',
          admin_password: '',
          confirm_password: ''
        });
      } else {
        const errorData: any = await response.json().catch(() => ({}));
        const detail = errorData?.detail;
        const text = typeof detail === 'string'
          ? detail
          : (detail ? JSON.stringify(detail) : 'Failed to create company. Please try again.');
        setMessage({ 
          type: 'error', 
          text
        });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Your Company Account</h2>
            <p className="text-gray-600">
              Get started with CosmaComply's free trial. No credit card required.
            </p>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-md ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Company Information</h3>
              
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your company name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="company_domain" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Domain (Optional)
                </label>
                <input
                  type="text"
                  id="company_domain"
                  name="company_domain"
                  value={formData.company_domain}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="yourcompany.com"
                />
              </div>
            </div>

            {/* Admin User Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Admin Account</h3>
              
              <div>
                <label htmlFor="admin_username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  id="admin_username"
                  name="admin_username"
                  value={formData.admin_username}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Choose a username"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="admin_email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="admin_email"
                  name="admin_email"
                  value={formData.admin_email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="admin@yourcompany.com"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="admin_full_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="admin_full_name"
                  name="admin_full_name"
                  value={formData.admin_full_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="admin_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  id="admin_password"
                  name="admin_password"
                  value={formData.admin_password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Minimum 8 characters"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Creating Account...' : 'Create Company Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-500 font-medium">
                Sign in here
              </Link>
            </p>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-md">
            <h4 className="text-sm font-medium text-gray-900 mb-2">What you get with your free trial:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 30-day free trial</li>
              <li>• Full access to all features</li>
              <li>• Up to 5 users</li>
              <li>• Unlimited facilities and products</li>
              <li>• FDA compliance tools</li>
              <li>• No credit card required</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySignup;
